import { Injectable, isDevMode } from '@angular/core';
import { AppConfig } from 'app/app.config';
import { Router } from '@angular/router';
import { EventAggregator, EventList } from 'app/shared/utility/EventAggregator';
import { SpinnerService } from 'app/shared/services/spinnerService';
import { Role } from 'app/shared/common/role';
import { User } from '../models/userModel';
import { API, EventTypes, Routes, StorageConstants } from 'app/shared/utility/constants';
import { DataService } from './dataService';

import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable()
export class AuthenticationService {
  private rootUrl: string;
  public tokenAPI;

  constructor(
    private router: Router,
    private appConfig: AppConfig,
    private dataService: DataService,
    private spinnerService: SpinnerService//, handler: HttpBackend
  ) {
    this.rootUrl = this.appConfig.getConfig('rootUrl');
  }

  forgotPassword(email: any) {
    this.spinnerService.showProcessingIndicator();
    // Failure and success respond the same way in order to prevent registered email searching
    this.dataService.httppost<boolean>(API.ForgetPassword,
      { email: email, emailConfirmation: email }, false)
      .then(_ => {
        this.spinnerService.hideProcessingIndicator();
        EventAggregator.publish(EventList.popupTriggered, {
          type: EventTypes.AlertMessage,
          value: {
            title: 'Password Reset',
            msg: 'An email regarding password reset has been sent to ' + email
          }
        });
      })
      .catch(_ => {
        this.spinnerService.hideProcessingIndicator();
        EventAggregator.publish(EventList.popupTriggered, {
          type: EventTypes.AlertMessage,
          value: { msg: 'Failed to reset password' }
        });
      });
  }

  public createUser(user: User): Promise<any> {
    this.spinnerService.showProcessingIndicator();

    return this.dataService.httppost(API.Register, user, false)
      .then(result => {
        this.spinnerService.hideProcessingIndicator();
        return result;
      })
      .catch(err => {
        this.spinnerService.hideProcessingIndicator();
        EventAggregator.publish(EventList.popupTriggered, {
          type: EventTypes.AlertMessage,
          value: { msg: 'Failed to create user' }
        });
        throw err;
      });
  }

  public requestConfirmationEmail(user: User): Promise<any> {
    this.spinnerService.showProcessingIndicator();

    return this.dataService.httppost(API.RequestConfirmation, user, false)
      .then(result => {
        this.spinnerService.hideProcessingIndicator();
        return result;
      })
      .catch(err => {
        this.spinnerService.hideProcessingIndicator();
        EventAggregator.publish(EventList.popupTriggered, {
          type: EventTypes.AlertMessage,
          value: { msg: 'RequestConfirmation failure.' }
        });
        throw err;
      });
  }

  public authenticate(email, password): Promise<any> {
    this.spinnerService.showProcessingIndicator();

    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    let options = { headers: headers };
    let body = new URLSearchParams({
      'username': email,
      'password': password
    });

    return this.dataService.httppostwithoptions('/Account/CreateToken', body, options)
      .then(resp => {
        return JSON.parse(JSON.stringify(resp));
      })
      .then(resp => {
        console.log("Response of authentication: ");
        console.log(resp);
        var token = resp.token;
        var expiry = resp.expires;
        var isTempPassword = resp.isTempPassword;

        sessionStorage.setItem(StorageConstants.BearerToken, token);
        sessionStorage.setItem(StorageConstants.TokenExpires, expiry);

        this.refreshToken();

        if (isTempPassword === "false") {
          this.router.navigate([Routes.Dashboard]);
        } else {
          return true;
        }
      })
      .catch(err => {
        var errorMessage = err.error.stack ? err.error.stack : "Unspecified authentication error.";
        this.spinnerService.hideProcessingIndicator();

        //let errorJson = err;
        EventAggregator.publish(EventList.popupTriggered, {
          type: 'alertMessage',
          value: { msg: errorMessage }
        });
      });
  }


  public getAccessToken(): string {
    let token = sessionStorage.getItem(StorageConstants.BearerToken);

    return (token !== 'undefined' && token) ? token : null;
  }

  public refreshToken() {
    clearInterval(Number(sessionStorage.getItem(StorageConstants.HeartbeatID)));
    let rootUrl = this.rootUrl;

    let intervalId = setInterval(() => {
      fetch(rootUrl + '/Account/RefreshToken', {
        headers: { Authorization: `Bearer ${sessionStorage.getItem(StorageConstants.BearerToken)}` }
      }).then(resp => resp.json())
        .then(resp => {
          var token = resp.token;
          var expires = resp.expires;

          sessionStorage.setItem(StorageConstants.BearerToken, token);
          sessionStorage.setItem(StorageConstants.TokenExpires, expires);
        }).catch(err => {
          console.log("Error while refressing token");
          console.log(err);
          this.logout();
        });
    }, 300 * 1000); // Five minute heartbeat
    console.log(intervalId);

    sessionStorage.setItem(StorageConstants.HeartbeatID, String(intervalId));
  }

  public isAuthenticated(): boolean {
    return (!this.getAccessToken()) ? false : !this.isTokenExpired();
  }

  public isTokenExpired() {
    let token = this.getAccessToken();
    let expiry = Number(sessionStorage.getItem(StorageConstants.TokenExpires));

    if (token && expiry) {
      const delayinMS = -5000;
      const now = (new Date()).getTime();

      let expired = expiry - now - delayinMS;

      return 0 > expired;
    }

    return true;
  }

  public logout(logoutOnServer: boolean = true) {
    this.dataService.clearAccess();
    sessionStorage.removeItem(StorageConstants.DashboardInit);

    this.logoutLocally();
  }

  private logoutLocally() {
    sessionStorage.removeItem(StorageConstants.BearerToken);
    sessionStorage.removeItem(StorageConstants.TokenExpires);

    clearInterval(Number(sessionStorage.getItem(StorageConstants.HeartbeatID)));
    sessionStorage.removeItem(StorageConstants.HeartbeatID);

    this.router.navigate(['/logout', { 'invalidSession': 'true' }]).then(() => {
      window.location.reload();
    })
  }

  public resetPassword(
    oldPassword,
    passwordReset,
    confirmPasswordReset
  ): Promise<any> {
    this.spinnerService.showProcessingIndicator();

    return this.dataService.httppost(
      '/Account/ChangePassword',
      {
        oldPassword: oldPassword,
        newPassword: passwordReset,
        confirmPassword: confirmPasswordReset
      }
    )
      .then(res => {
        this.spinnerService.hideProcessingIndicator();
        return res;
      })
      .catch(res => {
        this.spinnerService.hideProcessingIndicator();
        EventAggregator.publish(EventList.popupTriggered, {
          type: 'alertMessage',
          value: { msg: 'Failed to update password' }
        });
        return new Promise<any>(() => { });
      });
  }

  public emailResetPassword(
    code,
    email,
    password,
    confirmPassword
  ): Promise<any> {
    this.spinnerService.showProcessingIndicator();
    return this.dataService.httppost<boolean>('/Account/ResetPassword', {
      code,
      email,
      password,
      confirmPassword
    }, false)
      .then(_ => {
        this.spinnerService.hideProcessingIndicator();
        EventAggregator.publish(EventList.popupTriggered, {
          type: 'alertMessage',
          value: {
            title: 'Success!',
            msg: 'Successfully updated password! Please login to continue.'
          }
        });
        this.router.navigate(['/login']);
      })
      .catch(res => {
        this.spinnerService.hideProcessingIndicator();
        if(res.error) {
          let messages = res.error.message.split("\n");
          let title = res.error.title;
          let content = "The following conditions should be met: <br>";
          for(var i = 0; i < messages.length - 1; i++) {
            content += (i + 1) + ". " + messages[i] + " <br>";
          }
          EventAggregator.publish(EventList.popupTriggered, {
            type: 'alertMessage',
            value: {
              title: title,
              msg: content
            }
          });
        }
      });
  }
}